import DropHandler from './DropHandler';
import { sharedDragController } from 'javascripts/dnd/controllers/DragController';
import type DraggableItem from './DraggableItem';
import type ItemPlacement from 'components/framework/models/ItemPlacement';
import { type SelectableItemController } from './SelectableItem';

/**
 * Base class for handling common behavior for droppable list areas. A ListDropHandler
 * interacts with the shared DND controller state to drag items around a complex UI
 * without having to update any state in controller(s) directly.
 *
 * A ListDropHandler only needs to handle the final state/persisting updates after the
 * items are dropped, and does not need to worry about any state of the items while
 * they are being dragged.
 */
class ListDropHandler<
  Controller extends SelectableItemController = SelectableItemController
> extends DropHandler<Controller> {
  listKey: string;

  /**
   * Creates a new ListDropHandler.
   */
  constructor(params: { controller: Controller; listKey: string }) {
    const { controller, listKey } = params;

    super({ controller });

    this.listKey = listKey;
  }

  onHover({ draggedItem, hoverPercent }) {
    throw new Error(
      'Do not call onHover for ListDropHandler, use onReorder instead'
    );
  }

  /**
   * For reorderable items, called when a DraggableItem hovers over part of this DropHandler.
   *
   * Must provide a ItemPlacement, which specifies the relative position of the drag.
   */
  onReorder({
    draggedItem,
    placement,
  }: {
    draggedItem: DraggableItem<any>;
    placement: ItemPlacement;
  }) {
    const { listKey } = this;

    sharedDragController.actionHoverOverDropHandler({
      dropHandler: this,
      listKey,
      placement,
    });
  }

  async onDrop({ draggedItem }) {
    // We use the items from the DragController
    const { dragItems } = sharedDragController;
    const { placement } = sharedDragController.state.dropData;

    // Intentionally not await, finish the drop immediately, so that the UI updates immediately.
    this.onDropAtDropPlacement({ dragItems, placement });
  }

  /**
   * Called when items from a drag are dropped at a specific placement in the list. Ideally this method is
   * not overwritten in child classes, but the other methods that it calls out to are modified as needed.
   */
  async onDropAtDropPlacement({
    dragItems,
    placement,
  }: {
    dragItems: DraggableItem[];
    placement: ItemPlacement;
  }) {
    const dropHandler = this;
    const droppedItems = dragItems.map(draggedItem =>
      this.prepareDroppedItem(draggedItem)
    );

    dragItems.forEach((draggedItem, index) => {
      draggedItem.onEnd({ dropHandler });
    });

    this.updateSelection(droppedItems);

    // This is used to place the items at the drop location in the UI, but shouldn't save anything yet
    this.placeItemsBeforeDrop({
      dragItems,
      items: droppedItems,
      placement,
    });

    sharedDragController.actionStopDrag();

    // Persist the droppedItem
    const persistedItems = await Promise.all(
      droppedItems.map((droppedItem, index) => {
        const draggedItem = dragItems[index];
        return draggedItem.persistDroppedItem(droppedItem);
      })
    );

    // Now we can persist all changes to the items
    await this.persistDroppedItems({
      dragItems,
      items: persistedItems,
      placement,
    });

    dragItems.forEach(draggedItem => {
      draggedItem.onDropPersisted(dropHandler);
    });

    // Finally, reset the selection to the newly dropped items
    this.updateSelection(persistedItems);
  }

  /**
   * Place the items into the appropriate location in the controller. This is done before any saving
   * to provide immediate feedback that the drop has finished.
   */
  placeItemsBeforeDrop({
    dragItems,
    items,
    placement,
  }: {
    dragItems: DraggableItem[];
    items: any[];
    placement: ItemPlacement;
  }) {}

  /**
   * This is to finalize the drop. The dropped items have been saved, and now any final changes can be
   * made for the drop.
   */
  async persistDroppedItems({
    dragItems,
    items,
    placement,
  }: {
    dragItems: DraggableItem[];
    items: any[];
    placement: ItemPlacement;
  }) {}

  /**
   * Called when items are removed from the list. This occurs when a user drops on a void drop target.
   */
  onRemoveItems({ dragItems }: { dragItems: DraggableItem<any>[] }) {}
}

export default ListDropHandler;
